import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { BadgeState } from 'softcat:config/badge-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import { WidgetService } from 'softcat:services/widget.ts';
import { DelayUtils } from 'softcat:utils/delay.ts';
import { WidgetUtils } from 'softcat:utils/widget.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const SIGN_DELAY_MS = 3000;

export const AppsEdit = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const navigate = useNavigate();
    const { id = '', version = '' } = useParams<{ id: string; version: string }>();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ parentalControl: false, publisherCertificate: undefined, publisherCertificatePassword: '' });
    const certificateFileExtension = '.p12';

    const EditAppSchema = z
        .object({
            parentalControl: z.boolean(),
            publisherCertificate: z
                .instanceof(File)
                .refine(
                    (file) => {
                        return file.name.endsWith(certificateFileExtension);
                    },
                    { message: intl.formatMessage({ id: 'FORM_FILE_UPLOAD_VALIDATION_EXTENSION' }, { extensions: certificateFileExtension }) },
                )
                .optional(),
            publisherCertificatePassword: z.string().optional(),
        })
        .superRefine((data, context) => {
            if (!data.publisherCertificate) {
                return true;
            } else if (!data.publisherCertificatePassword) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: intl.formatMessage({ id: 'APPS_EDIT_PAGE_FORM_PUBLISHER_CERTIFICATE_PASSWORD_VALIDATION_REQUIRED' }),
                    path: ['publisherCertificatePassword'],
                });
            }
        });

    const { data: platformsData = [] } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllPlatforms` }, ConfigService.findAllPlatforms);
    const {
        data: appData,
        error: appDataError,
        isLoading: appDataIsLoading,
    } = useSWR({ id, key: `${activeTerritory}-WidgetServiceFindOne`, version }, WidgetService.findOne);

    useEffect(() => {
        if (appData) {
            setFormValues({
                parentalControl: appData.parentalControl,
                publisherCertificate: undefined,
                publisherCertificatePassword: '',
            });
        }
    }, [appData]);

    const onSubmit = async (values: z.infer<typeof EditAppSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (appData) {
            try {
                await WidgetService.update({
                    ...appData,
                    parentalControl: values.parentalControl,
                });

                if (values.publisherCertificate && values.publisherCertificatePassword) {
                    // Signing starts after the saved edit has been applied everywhere.
                    await DelayUtils.wait({ milliseconds: SIGN_DELAY_MS });

                    await WidgetService.sign({
                        file: values.publisherCertificate,
                        id: appData.id,
                        password: values.publisherCertificatePassword,
                        version: appData.version,
                    });
                }

                setSubmitting(false);

                return navigate('/apps');
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditAppSchema>> defaultValues={formValues} mode={'onBlur'} onSubmit={onSubmit} resolver={zodResolver(EditAppSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header description={<FormattedMessage id={'APPS_EDIT_PAGE_DESCRIPTION'} />} title={<FormattedMessage id={'APPS_EDIT_PAGE_HEADING'} />} />

                {appDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'APPS_EDIT_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {appDataIsLoading && <Spinner />}

                {appData && (
                    <>
                        <Form.StaticValue label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_NAME_LABEL'} />} value={appData.name} />

                        <Form.StaticValue label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_ID_LABEL'} />} value={appData.id} />

                        <Form.StaticValue label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_VERSION_LABEL'} />} value={appData.version} />

                        <Form.StaticValue label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_CONFIG_VERSION_LABEL'} />} value={appData.configVersion} />

                        <Form.Checkbox label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_PARENTAL_CONTROL_LABEL'} />} name={'parentalControl'} />

                        <Form.StaticValue
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_EXISTING_CERTIFICATE_ISSUER_LABEL'} />}
                            value={appData.publisherIssuerDn}
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_EXISTING_CERTIFICATE_SUBJECT_LABEL'} />}
                            value={appData.publisherSubjectDn}
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_EXISTING_CERTIFICATE_ALGORITHM_LABEL'} />}
                            value={appData.publisherSigAlgName}
                        />

                        <Form.FileUpload
                            accept={certificateFileExtension}
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_PUBLISHER_CERTIFICATE_LABEL'} />}
                            name={'publisherCertificate'}
                        />

                        <Form.Input
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_PUBLISHER_CERTIFICATE_PASSWORD_LABEL'} />}
                            name={'publisherCertificatePassword'}
                            type={'password'}
                        />

                        <Form.StaticBadges
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_PLATFORMS_LABEL'} />}
                            status={BadgeState.INFO}
                            values={WidgetUtils.matchPlatforms({
                                platforms: platformsData,
                                widget: appData,
                            })}
                        />

                        <Form.StaticBadges
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_VARIANTS_LABEL'} />}
                            status={BadgeState.REVIEW}
                            values={WidgetUtils.extractPlatformVariants({ widget: appData })}
                        />

                        <Form.StaticBadges
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_REGIONS_LABEL'} />}
                            status={BadgeState.READY}
                            values={WidgetUtils.extractRegions({ widget: appData })}
                        />

                        <Form.StaticBadges
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_PROPOSITIONS_LABEL'} />}
                            status={BadgeState.INACTIVE}
                            values={WidgetUtils.extractPropositions({ widget: appData })}
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_UPLOADED_LABEL'} />}
                            value={
                                <span className={'flex items-center gap-1 text-violet-400'}>
                                    <ArrowUpCircleIcon className={'inline-block size-4'} />
                                    <span data-testid={'uploaded-details'}>
                                        <FormattedMessage
                                            id={'APPS_EDIT_PAGE_FORM_UPLOADED_BY'}
                                            values={{
                                                date: new Date(appData.uploadedDate).toLocaleString(undefined, {
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    hour12: false,
                                                    minute: '2-digit',
                                                    month: 'short',
                                                    second: '2-digit',
                                                    weekday: 'short',
                                                    year: 'numeric',
                                                }),
                                                username: appData.uploadedBy,
                                            }}
                                        />
                                    </span>
                                </span>
                            }
                        />

                        <Form.StaticValue
                            label={<FormattedMessage id={'APPS_EDIT_PAGE_FORM_LOCATION_URL'} />}
                            value={
                                appData.widgetLocation ? (
                                    <a
                                        className={'inline-flex items-center gap-2 transition-colors hover:text-indigo-600'}
                                        href={appData.widgetLocation}
                                        rel="noreferrer"
                                        target={'_blank'}
                                    >
                                        <ArrowDownTrayIcon className={'inline-block size-4'} /> {appData.widgetLocation}
                                    </a>
                                ) : (
                                    '-'
                                )
                            }
                        />

                        <Form.Footer
                            isSubmitting={submitting}
                            onCancel={() => {
                                return navigate('/apps');
                            }}
                            submitError={onEditError && <FormattedMessage id={'APPS_EDIT_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
